import React, { useState } from 'react';
import Button from '../../../components/base/Button';

// Razorpay types
declare global {
  interface Window {
    Razorpay: any;
  }
}

export default function FinalCTASection() {
  const [showDonationForm, setShowDonationForm] = useState(false);
  const [formData, setFormData] = useState({
    amount: '',
    customAmount: '',
    frequency: 'one-time',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    pincode: '',
    panNumber: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleCSRPartnership = () => {
    const csrSection = document.getElementById('csr-partnerships-section');
    if (csrSection) {
      csrSection.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  const handleDonateNow = () => {
    setShowDonationForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const finalAmount = formData.amount === 'custom' ? formData.customAmount : formData.amount;
    if (!finalAmount || !formData.firstName || !formData.email) return;

    setIsSubmitting(true);
    
    try {
      // Initialize Razorpay
      const options = {
        key: 'rzp_live_SLW5EZjmEu3Hpf',
        amount: parseInt(finalAmount) * 100, // Convert to paise
        currency: 'INR',
        name: 'Diatomic Life Foundation',
        description: `${formData.frequency === 'monthly' ? 'Monthly' : 'One-time'} Donation`,
        image: 'https://public.readdy.ai/ai/img_res/ddf9244497b7103272b6ef9700a82aba.jpg',
        prefill: {
          name: `${formData.firstName} ${formData.lastName}`,
          email: formData.email,
          contact: formData.phone
        },
        notes: {
          frequency: formData.frequency,
          address: formData.address,
          city: formData.city,
          pincode: formData.pincode,
          panNumber: formData.panNumber
        },
        theme: {
          color: '#12B6B6'
        },
        handler: async function (response: any) {
          // Payment successful
          console.log('Payment successful:', response);
          
          // Submit form data to Readdy form
          const formDataToSubmit = new URLSearchParams();
          formDataToSubmit.append('amount', finalAmount);
          formDataToSubmit.append('frequency', formData.frequency);
          formDataToSubmit.append('firstName', formData.firstName);
          formDataToSubmit.append('lastName', formData.lastName);
          formDataToSubmit.append('email', formData.email);
          formDataToSubmit.append('phone', formData.phone);
          formDataToSubmit.append('address', formData.address);
          formDataToSubmit.append('city', formData.city);
          formDataToSubmit.append('pincode', formData.pincode);
          formDataToSubmit.append('panNumber', formData.panNumber);
          formDataToSubmit.append('paymentId', response.razorpay_payment_id);
          formDataToSubmit.append('paymentStatus', 'Success');

          await fetch('https://readdy.ai/api/form/d31ru9oemi0jo797cf70', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: formDataToSubmit.toString(),
          });

          setIsSubmitted(true);
          setFormData({
            amount: '',
            customAmount: '',
            frequency: 'one-time',
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            address: '',
            city: '',
            pincode: '',
            panNumber: ''
          });
        },
        modal: {
          ondismiss: function() {
            setIsSubmitting(false);
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
      
    } catch (error) {
      console.error('Payment initialization error:', error);
      alert('Unable to initialize payment. Please try again.');
      setIsSubmitting(false);
    }
  };

  const presetAmounts = ['5000', '10000', '25000', '50000'];

  return (
    <>
      <section className="py-24 bg-black text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-5xl md:text-6xl font-bold mb-8 leading-tight">
            Support Oxygen Access.
            <br />
            <span className="text-[#12B6B6]">Sponsor Awareness.</span>
            <br />
            Enable Prevention.
          </h2>

          <p className="text-xl text-gray-300 mb-12 max-w-3xl mx-auto leading-relaxed">
            Every donation brings us closer to a world where oxygen deficiency
            is detected early and treated effectively.
          </p>

          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Button
              variant="primary"
              size="lg"
              className="px-12 py-4 text-lg bg-[#12B6B6] hover:bg-[#0E9999] border-[#12B6B6]"
              onClick={handleDonateNow}
            >
              Donate Now (Individuals)
            </Button>
            <Button
              variant="secondary"
              size="lg"
              className="px-12 py-4 text-lg bg-transparent border-2 border-white text-white hover:bg-white hover:text-black"
              onClick={handleCSRPartnership}
            >
              CSR Partnership
            </Button>
          </div>

          {/* Bottom Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16 pt-12 border-t border-gray-800">
            <div className="text-center">
              <div className="text-3xl font-bold text-[#12B6B6] mb-2">0</div>
              <div className="text-gray-400">Families Ready to be Supported</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-[#12B6B6] mb-2">₹0</div>
              <div className="text-gray-400">Funds Ready for Deployment</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-[#12B6B6] mb-2">100%</div>
              <div className="text-gray-400">Transparency Commitment</div>
            </div>
          </div>

          {/* Trust Footer */}
          <div className="mt-12 pt-8 border-t border-gray-800">
            <div className="flex items-center justify-center gap-2 text-gray-400 text-sm">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-government-line text-[#12B6B6]"></i>
              </div>
              <span>Diatomic Life Foundation is a registered Section-8 non-profit with 80G tax exemption eligibility.</span>
            </div>
          </div>

        </div>
      </section>

      {/* Donation Form Modal */}
      {showDonationForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-8">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-[#111111]">Make a Donation</h3>
                <button 
                  onClick={() => setShowDonationForm(false)}
                  className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 cursor-pointer"
                >
                  <i className="ri-close-line text-gray-600"></i>
                </button>
              </div>

              {isSubmitted ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
                    <i className="ri-check-line text-4xl text-green-600"></i>
                  </div>
                  <h4 className="text-2xl font-bold text-[#111111] mb-4">Thank You!</h4>
                  <p className="text-lg text-gray-700 mb-6">
                    Your donation has been processed successfully. You'll receive an 80G tax receipt via email within 24 hours.
                  </p>
                  <div className="bg-[#12B6B6]/10 rounded-xl p-6">
                    <div className="flex items-center justify-center space-x-4 text-[#12B6B6]">
                      <i className="ri-shield-check-line text-2xl"></i>
                      <div className="text-left">
                        <div className="font-semibold">Secure Payment Processed</div>
                        <div className="text-sm">80G Tax Receipt Coming Soon</div>
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="primary"
                    className="mt-6"
                    onClick={() => {
                      setIsSubmitted(false);
                      setShowDonationForm(false);
                    }}
                  >
                    Close
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} id="individual-donation" data-readdy-form className="space-y-6">
                  {/* Amount Selection */}
                  <div>
                    <label className="block text-sm font-medium text-[#111111] mb-3">Donation Amount</label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                      {presetAmounts.map((amount) => (
                        <button
                          key={amount}
                          type="button"
                          onClick={() => setFormData({...formData, amount, customAmount: ''})}
                          className={`p-3 rounded-xl border-2 text-center font-semibold transition-all ${
                            formData.amount === amount 
                              ? 'border-[#12B6B6] bg-[#12B6B6] text-white' 
                              : 'border-gray-200 hover:border-[#12B6B6] text-gray-700'
                          }`}
                        >
                          ₹{parseInt(amount).toLocaleString()}
                        </button>
                      ))}
                    </div>
                    <div className="flex items-center space-x-3">
                      <button
                        type="button"
                        onClick={() => setFormData({...formData, amount: 'custom'})}
                        className={`px-4 py-2 rounded-lg border-2 font-semibold transition-all whitespace-nowrap ${
                          formData.amount === 'custom' 
                            ? 'border-[#12B6B6] bg-[#12B6B6] text-white' 
                            : 'border-gray-200 hover:border-[#12B6B6] text-gray-700'
                        }`}
                      >
                        Custom
                      </button>
                      {formData.amount === 'custom' && (
                        <input
                          type="number"
                          name="customAmount"
                          placeholder="Enter amount"
                          value={formData.customAmount}
                          onChange={(e) => setFormData({...formData, customAmount: e.target.value})}
                          className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#12B6B6] focus:border-transparent text-sm"
                          min="100"
                        />
                      )}
                    </div>
                  </div>

                  {/* Frequency Selection */}
                  <div>
                    <label className="block text-sm font-medium text-[#111111] mb-3">Donation Frequency</label>
                    <div className="flex space-x-4">
                      <button
                        type="button"
                        onClick={() => setFormData({...formData, frequency: 'one-time'})}
                        className={`px-6 py-3 rounded-xl font-semibold transition-all whitespace-nowrap ${
                          formData.frequency === 'one-time' 
                            ? 'bg-[#12B6B6] text-white' 
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        One-time
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData({...formData, frequency: 'monthly'})}
                        className={`px-6 py-3 rounded-xl font-semibold transition-all whitespace-nowrap ${
                          formData.frequency === 'monthly' 
                            ? 'bg-[#12B6B6] text-white' 
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        Monthly
                      </button>
                    </div>
                  </div>

                  {/* Personal Information */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#111111] mb-2">First Name</label>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#12B6B6] focus:border-transparent text-sm"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#111111] mb-2">Last Name</label>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#12B6B6] focus:border-transparent text-sm"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#111111] mb-2">Email Address</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#12B6B6] focus:border-transparent text-sm"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#111111] mb-2">Phone Number</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#12B6B6] focus:border-transparent text-sm"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#111111] mb-2">Address</label>
                    <input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={(e) => setFormData({...formData, address: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#12B6B6] focus:border-transparent text-sm"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#111111] mb-2">City</label>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={(e) => setFormData({...formData, city: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#12B6B6] focus:border-transparent text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#111111] mb-2">PIN Code</label>
                      <input
                        type="text"
                        name="pincode"
                        value={formData.pincode}
                        onChange={(e) => setFormData({...formData, pincode: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#12B6B6] focus:border-transparent text-sm"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#111111] mb-2">PAN Number (for 80G receipt)</label>
                    <input
                      type="text"
                      name="panNumber"
                      value={formData.panNumber}
                      onChange={(e) => setFormData({...formData, panNumber: e.target.value.toUpperCase()})}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#12B6B6] focus:border-transparent text-sm"
                      placeholder="ABCDE1234F"
                    />
                  </div>

                  <div className="flex space-x-4 pt-6">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowDonationForm(false)}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={isSubmitting || !formData.firstName || !formData.email || !formData.phone || (!formData.amount && !formData.customAmount)}
                      className="flex-1"
                    >
                      {isSubmitting ? 'Processing...' : 'Proceed to Payment'}
                    </Button>
                  </div>

                  <div className="bg-gray-50 rounded-xl p-4 mt-6">
                    <div className="flex items-start space-x-3">
                      <i className="ri-shield-check-line text-[#12B6B6] text-xl mt-1"></i>
                      <div>
                        <div className="font-semibold text-[#111111] mb-1">Secure & Tax Deductible</div>
                        <div className="text-sm text-gray-600">
                          Your donation is processed securely via Razorpay and you'll receive an 80G tax receipt via email.
                        </div>
                      </div>
                    </div>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
